import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  message,
} from 'antd';
import dayjs from 'dayjs';
import {
  createSimplifiedOptionHistoryAdmin,
  fetchSimplifiedOptionHistoryAdmin,
  fetchSimplifiedOptionsAdmin,
  SimplifiedOptionAdminAsset,
  SimplifiedOptionHistoryAdminRow,
  updateSimplifiedOptionHistoryAdmin,
} from './adminDataApi';
import SimplifiedOptionAuditTables from './SimplifiedOptionAuditTables';
import SimplifiedOptionCurveCsvUpload from './SimplifiedOptionCurveCsvUpload';

/** Lets PM admins supply canonical sparse curves and inspect immutable runtime audit facts. */
const SimplifiedOptionDataTab: React.FC = () => {
  const [form] = Form.useForm();
  const [options, setOptions] = useState<SimplifiedOptionAdminAsset[]>([]);
  const [selectedId, setSelectedId] = useState<number>();
  const [history, setHistory] = useState<SimplifiedOptionHistoryAdminRow[]>([]);
  const [editingId, setEditingId] = useState<number>();
  const [loading, setLoading] = useState(false);

  /** Loads the selected asset's curve rows after a create, edit, or selection change. */
  const loadHistory = useCallback(async (optionId: number) => {
    setLoading(true);
    try {
      setHistory(await fetchSimplifiedOptionHistoryAdmin(optionId));
    } catch (error) {
      message.error(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    fetchSimplifiedOptionsAdmin()
      .then(result => {
        if (!active) return;
        setOptions(result);
        if (result.length) setSelectedId(result[0].id);
      })
      .catch(error =>
        message.error(error instanceof Error ? error.message : String(error)),
      );
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (selectedId) loadHistory(selectedId);
  }, [selectedId, loadHistory]);

  if (!options.length) {
    return (
      <Empty description="Create a SimplifiedOption in Data > Assets first." />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="Supplied strike, volatility, and BP curves">
        <Select
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 360, marginBottom: 16 }}
          value={selectedId}
          onChange={value => {
            setSelectedId(value);
            setEditingId(undefined);
            form.resetFields();
          }}
          options={options.map(option => ({
            value: option.id,
            label: `${option.ticker} — ${option.issued_delta}Δ ${
              option.option_type === 0 ? 'Call' : 'Put'
            }`,
          }))}
        />
        <div style={{ marginBottom: 16 }}>
          <SimplifiedOptionCurveCsvUpload
            simplifiedOptionId={selectedId}
            onUploaded={() =>
              selectedId ? loadHistory(selectedId) : Promise.resolve()
            }
          />
        </div>
        <Form
          form={form}
          layout="inline"
          onFinish={async values => {
            if (!selectedId) return;
            const payload = {
              simplified_option: selectedId,
              timestamp: values.timestamp.toISOString(),
              strike: values.strike ?? null,
              annualized_implied_volatility:
                values.annualized_implied_volatility ?? null,
              basis_points: values.basis_points ?? null,
              source: values.source || '',
              source_reference: values.source_reference || '',
            };
            try {
              if (editingId) {
                await updateSimplifiedOptionHistoryAdmin({
                  ...payload,
                  id: editingId,
                });
              } else {
                await createSimplifiedOptionHistoryAdmin(payload);
              }
              message.success(
                editingId ? 'Curve row updated.' : 'Curve row added.',
              );
              setEditingId(undefined);
              form.resetFields();
              await loadHistory(selectedId);
            } catch (error) {
              message.error(
                error instanceof Error ? error.message : String(error),
              );
            }
          }}
        >
          <Form.Item
            name="timestamp"
            rules={[{ required: true, message: 'Timestamp is required.' }]}
          >
            <DatePicker showTime placeholder="Curve timestamp" />
          </Form.Item>
          <Form.Item name="strike">
            <InputNumber min={0} placeholder="Strike" />
          </Form.Item>
          <Form.Item name="annualized_implied_volatility">
            <InputNumber
              min={0}
              placeholder="Annualized vol (0.20)"
              step={0.01}
            />
          </Form.Item>
          <Form.Item name="basis_points">
            <InputNumber placeholder="Basis points" step={0.01} />
          </Form.Item>
          <Form.Item name="source">
            <Input placeholder="Source" />
          </Form.Item>
          <Form.Item name="source_reference">
            <Input placeholder="Source reference" />
          </Form.Item>
          <Form.Item shouldUpdate>
            {() => {
              const strike = form.getFieldValue('strike');
              const volatility = form.getFieldValue(
                'annualized_implied_volatility',
              );
              const basisPoints = form.getFieldValue('basis_points');
              return (
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={
                    strike == null && volatility == null && basisPoints == null
                  }
                >
                  {editingId ? 'Save row' : 'Add row'}
                </Button>
              );
            }}
          </Form.Item>
          {editingId && (
            <Button
              onClick={() => {
                setEditingId(undefined);
                form.resetFields();
              }}
            >
              Cancel edit
            </Button>
          )}
        </Form>
        <Table
          style={{ marginTop: 16 }}
          rowKey="id"
          size="small"
          bordered
          loading={loading}
          dataSource={history}
          columns={[
            { title: 'Timestamp', dataIndex: 'timestamp' },
            {
              title: 'Strike',
              dataIndex: 'strike',
              render: value => value ?? '-',
            },
            {
              title: 'Annualized vol',
              dataIndex: 'annualized_implied_volatility',
              render: value => value ?? '-',
            },
            {
              title: 'Basis points',
              dataIndex: 'basis_points',
              render: value => value ?? '-',
            },
            {
              title: 'Source',
              dataIndex: 'source',
              render: value => value || '-',
            },
            {
              title: 'Reference',
              dataIndex: 'source_reference',
              render: value => value || '-',
            },
            {
              title: 'Actions',
              render: (_, row) => (
                <Button
                  type="link"
                  onClick={() => {
                    setEditingId(row.id);
                    form.setFieldsValue({
                      ...row,
                      timestamp: dayjs(row.timestamp),
                    });
                  }}
                >
                  Edit
                </Button>
              ),
            },
          ]}
        />
      </Card>
      <Card title="Immutable option audit">
        <SimplifiedOptionAuditTables simplifiedOptionId={selectedId} />
      </Card>
    </Space>
  );
};

export default SimplifiedOptionDataTab;
